import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

function epochDate(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value);
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)) {
    const n = Number(value);
    if (Number.isFinite(n)) return new Date(n);
  }
  return null;
}

function formatXAxis(value) {
  const date = epochDate(value);
  if (!date) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatTooltipLabel(value) {
  const date = epochDate(value);
  if (!date) return '';
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function TimeSeriesChart({
  data = [],
  lines = [],
  title,
  type = 'line',
  yAxisLabel,
  xAxisLabel,
  showLegend = true,
  height = 300,
  yDomain,
  xDomain,
  referenceY,
  band,
}) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
        <p className="text-sm">No data available for chart</p>
      </div>
    );
  }

  const formatTooltipValue = (value, name) => {
    if (typeof value !== 'number') return value;
    if (
      name?.toLowerCase().includes('utilization') ||
      name?.toLowerCase().includes('percent') ||
      name?.toLowerCase().includes('mean')
    ) {
      return `${value.toFixed(1)}%`;
    }
    if (name?.toLowerCase().includes('time') || name?.toLowerCase().includes('ms')) {
      return `${value.toFixed(0)}ms`;
    }
    return value.toFixed(2);
  };

  const ChartComponent = band || referenceY != null
    ? ComposedChart
    : type === 'area'
      ? AreaChart
      : LineChart;
  const DataComponent = type === 'area' && !band ? Area : Line;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {title && (
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            type="number"
            dataKey="tMs"
            domain={xDomain || ['dataMin', 'dataMax']}
            interval="preserveStartEnd"
            minTickGap={40}
            tickFormatter={formatXAxis}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={{ stroke: '#d1d5db' }}
            axisLine={{ stroke: '#d1d5db' }}
            label={xAxisLabel ? { value: xAxisLabel, position: 'bottom', offset: -5 } : undefined}
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={{ stroke: '#d1d5db' }}
            axisLine={{ stroke: '#d1d5db' }}
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
            labelFormatter={formatTooltipLabel}
            formatter={formatTooltipValue}
          />
          {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
            />
          )}
          {band && (
            <>
              <Area
                type="monotone"
                dataKey={band.min}
                stackId="band"
                stroke="none"
                fill="transparent"
                connectNulls={false}
                legendType="none"
                tooltipType="none"
              />
              <Area
                type="monotone"
                dataKey={band.span}
                name={band.name || 'Min–max'}
                stackId="band"
                stroke="none"
                fill={band.color || '#8b5cf6'}
                fillOpacity={0.22}
                connectNulls={false}
                tooltipType="none"
              />
            </>
          )}
          {referenceY != null && (
            <ReferenceLine
              y={referenceY}
              stroke="#7c3aed"
              strokeDasharray="4 4"
              label={{ value: `Peak ${Number(referenceY).toFixed(1)}%`, fill: '#6b21a8', fontSize: 11 }}
            />
          )}
          {lines.map((line) => (
            <DataComponent
              key={line.key}
              type="monotone"
              dataKey={line.key}
              name={line.name}
              stroke={line.color}
              fill={type === 'area' && !band ? line.color : undefined}
              fillOpacity={type === 'area' && !band ? 0.3 : undefined}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
              activeDot={{ r: 4, strokeWidth: 2 }}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
}
